/** @typedef {{ param_name: string, param_value: string, h1: string, title: string, description: string }} IndexableFilterEntry */

const SELECTORS = {
  indexableData: '#indexable-filters-data',
  collectionTitle: '[data-collection-title]',
  collectionDescription: '[data-collection-description]',
  originalDescription: '#original-collection-description',
  originalH1: 'meta[name="original-collection-h1"]',
  originalTitle: 'meta[name="original-title"]',
};

const TITLE_SUFFIX = ' | WARAGOD';

/**
 * @returns {IndexableFilterEntry[]}
 */
function parseIndexableFilters() {
  const dataEl = document.querySelector(SELECTORS.indexableData);
  if (!(dataEl instanceof HTMLScriptElement) || !dataEl.textContent?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(dataEl.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} paramName
 * @returns {string}
 */
function getFilterGroupKey(paramName) {
  return paramName.replace(/\.(gte|lte)$/, '');
}

/**
 * @param {URLSearchParams} searchParams
 * @returns {Map<string, { param_name: string, value: string }[]>}
 */
function groupFilterParams(searchParams) {
  /** @type {Map<string, { param_name: string, value: string }[]>} */
  const groups = new Map();

  for (const [paramName, value] of searchParams.entries()) {
    if (!paramName.startsWith('filter.') || value === '') continue;

    const groupKey = getFilterGroupKey(paramName);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)?.push({ param_name: paramName, value });
  }

  return groups;
}

/**
 * @param {string} value
 * @returns {string}
 */
function decodeFilterParamValue(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * @param {string} activeValue
 * @param {string} configuredValue
 * @returns {boolean}
 */
function paramValuesMatch(activeValue, configuredValue) {
  if (activeValue === configuredValue) return true;

  return decodeFilterParamValue(activeValue) === decodeFilterParamValue(configuredValue);
}

/**
 * @param {URLSearchParams} searchParams
 * @param {IndexableFilterEntry[]} indexableFilters
 * @returns {IndexableFilterEntry | null}
 */
function resolveMatch(searchParams, indexableFilters) {
  if (indexableFilters.length === 0) return null;

  const groups = groupFilterParams(searchParams);
  const activeGroups = [...groups.values()].filter((group) => group.length > 0);

  const soleGroup = activeGroups[0];
  if (activeGroups.length !== 1 || !soleGroup || soleGroup.length !== 1) {
    return null;
  }

  const soleValue = soleGroup[0];
  if (!soleValue) return null;

  const { param_name, value } = soleValue;
  return (
    indexableFilters.find(
      (entry) => entry.param_name === param_name && paramValuesMatch(value, entry.param_value)
    ) ?? null
  );
}

/**
 * @returns {string}
 */
function getOriginalDescriptionHtml() {
  const template = document.querySelector(SELECTORS.originalDescription);
  if (!(template instanceof HTMLTemplateElement)) return '';

  const description = template.content.querySelector(SELECTORS.collectionDescription);
  return description instanceof HTMLElement ? description.innerHTML : template.innerHTML;
}

/**
 * @returns {string}
 */
function buildDefaultTitle() {
  const originalTitle = document.querySelector(SELECTORS.originalTitle);
  if (originalTitle instanceof HTMLMetaElement && originalTitle.content) {
    return originalTitle.content;
  }

  return document.title;
}

/**
 * @param {string} title
 */
function setDocumentTitle(title) {
  if (document.title !== title) {
    document.title = title;
  }
}

/**
 * @param {string} h1
 */
function setCollectionTitle(h1) {
  const el = document.querySelector(SELECTORS.collectionTitle);
  if (el && el.textContent !== h1) {
    el.textContent = h1;
  }
}

/**
 * @param {string} html
 */
function setCollectionDescription(html) {
  const el = document.querySelector(SELECTORS.collectionDescription);
  if (!el || el.innerHTML === html) return;

  el.innerHTML = html;

  if (typeof FoxTheme !== 'undefined' && typeof FoxTheme.utils?.resetReadMore === 'function') {
    FoxTheme.utils.resetReadMore(el);
  }
}

function applyOriginal() {
  const originalH1 = document.querySelector(SELECTORS.originalH1);
  if (originalH1 instanceof HTMLMetaElement) {
    setCollectionTitle(originalH1.content);
  }

  setCollectionDescription(getOriginalDescriptionHtml());
  setDocumentTitle(buildDefaultTitle());
}

/**
 * @param {IndexableFilterEntry} entry
 */
function applyIndexable(entry) {
  if (entry.h1) {
    setCollectionTitle(entry.h1);
  }

  const description = entry.description?.trim();
  if (description) {
    setCollectionDescription(description);
  } else {
    setCollectionDescription(getOriginalDescriptionHtml());
  }

  if (entry.title) {
    setDocumentTitle(`${entry.title}${TITLE_SUFFIX}`);
  }
}

/**
 * @param {URLSearchParams} searchParams
 */
function updateFromSearchParams(searchParams) {
  const indexableFilters = parseIndexableFilters();
  const match = resolveMatch(searchParams, indexableFilters);

  if (match) {
    applyIndexable(match);
  } else {
    applyOriginal();
  }
}

function initIndexableFilters() {
  if (!document.querySelector(SELECTORS.indexableData)) return;

  if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub?.PUB_SUB_EVENTS?.facetUpdate) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, (data) => {
      const queryParams =
        data?.queryParams instanceof URLSearchParams
          ? data.queryParams
          : new URLSearchParams(location.search);

      updateFromSearchParams(queryParams);
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initIndexableFilters);
} else {
  initIndexableFilters();
}
